/* eslint-disable arrow-body-style */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import JsxParser from 'react-jsx-parser';

import { applyCheckoutCouponGql, removeCheckoutCouponGql } from '../gql';
import { formatNumber } from '../helper';
import InputField from '../../../components/InputField';
import PaymentSection from './PaymentSection';
import ExternalLink from '../../../components/ExternalLink';

export default function Review({
  checkout,
  values,
  setStripe,
  setStripeElements,
  refetchParentCheckout,
  details,
  paymentMethodOrder,
}) {
  const { t } = useTranslation('checkout');
  const params = useParams();
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [couponBusy, setCouponBusy] = useState(false);
  const [couponOpen, setCouponOpen] = useState(false);

  const [applyCheckoutCoupon] = useMutation(applyCheckoutCouponGql, {
    variables: { slug: params.slug },
  });

  const [removeCheckoutCoupon] = useMutation(removeCheckoutCouponGql, {
    variables: { slug: params.slug },
  });

  const appliedCoupon = useMemo(() => {
    const lineItems = checkout?.lineItems || [];
    for (const lineItem of lineItems) {
      const coupon = lineItem?.coupons?.[0];
      if (coupon?.code) return coupon;
    }
    return null;
  }, [checkout?.lineItems]);

  useEffect(() => {
    if (appliedCoupon?.code) {
      setCouponCode(appliedCoupon.code);
      setCouponOpen(true);
    } else if (!couponBusy) {
      setCouponCode('');
    }
  }, [appliedCoupon?.code, couponBusy]);

  const tosLabelRaw = details?.event?.frontendSettings?.tosLabel;
  const couponAbovePayment = details?.event?.frontendSettings?.couponAbovePayment ?? false;
  const isNetPriceModel = details?.event?.priceModel === 'net';
  const taxLabel = isNetPriceModel ? 'MwSt. 19%' : 'Enthaltene MwSt (19%)';
  const tosLabel = (typeof tosLabelRaw === 'string' && tosLabelRaw.trim().length > 0)
    ? (
      <JsxParser
        components={{ ExternalLink, Link: ExternalLink, a: ExternalLink }}
        jsx={tosLabelRaw}
      />
    )
    : t('acceptTOS');

  const handleApplyCoupon = async () => {
    const trimmed = couponCode.trim();
    if (!trimmed || appliedCoupon) return;
    setCouponBusy(true);
    setCouponError('');
    try {
      await applyCheckoutCoupon({
        variables: { slug: params.slug, input: { code: trimmed } },
      });
      setCouponCode('');
      if (refetchParentCheckout) await refetchParentCheckout();
    } catch (error) {
      const errorCode = error?.networkError?.result?.code || error?.cause?.result?.code;
      const errorKey = errorCode ? `coupon.errors.${errorCode}` : 'coupon.error';
      setCouponError(t([errorKey, 'coupon.error']));
    } finally {
      setCouponBusy(false);
    }
  };

  const handleRemoveCoupon = async () => {
    const code = appliedCoupon?.code || couponCode.trim();
    if (!code) return;
    setCouponBusy(true);
    setCouponError('');
    try {
      await removeCheckoutCoupon({
        variables: { slug: params.slug, input: { code } },
      });
      if (refetchParentCheckout) await refetchParentCheckout();
    } catch (error) {
      const errorCode = error?.networkError?.result?.code || error?.cause?.result?.code;
      const errorKey = errorCode ? `coupon.errors.${errorCode}` : 'coupon.error';
      setCouponError(t([errorKey, 'coupon.error']));
    } finally {
      setCouponBusy(false);
    }
  };

  const couponAccordion = (
    <Accordion
      sx={{ width: '100%' }}
      expanded={Boolean(appliedCoupon) || couponOpen}
      onChange={(_event, isExpanded) => {
        if (!appliedCoupon) setCouponOpen(isExpanded);
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1">{t('coupon.label')}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              value={couponCode}
              onChange={(event) => setCouponCode(event.target.value)}
              label={t('coupon.placeholder')}
              fullWidth
              size="small"
              disabled={couponBusy || Boolean(appliedCoupon)}
            />
            {appliedCoupon ? (
              <Button
                variant="outlined"
                onClick={handleRemoveCoupon}
                disabled={couponBusy}
                color="error"
              >
                {t('coupon.remove')}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleApplyCoupon}
                disabled={!couponCode.trim() || couponBusy}
                color="primary"
              >
                {t('coupon.apply')}
              </Button>
            )}
          </Stack>
          {couponError && (
            <Typography variant="body2" sx={{ color: 'error.main' }}>
              {couponError}
            </Typography>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ marginBottom: 4 }}>
        {t('steps.review')}
      </Typography>
      <Stack
        direction="column"
        divider={<Divider flexItem />}
        spacing={2}
        sx={{ my: 2 }}
      >
        <div>
          <Typography variant="h6" gutterBottom>
            Rechnungsadresse
          </Typography>
          {values.invoiceAddress.company && (
            <Typography gutterBottom>{values.invoiceAddress.company}</Typography>
          )}
          <Typography gutterBottom>{values.invoiceAddress.forename} {values.invoiceAddress.name}</Typography>
          <Typography sx={{ color: 'text.secondary' }}>
            {values.invoiceAddress.address1}
          </Typography>
          <Typography gutterBottom sx={{ color: 'text.secondary' }}>
            {values.invoiceAddress.postalCode} {values.invoiceAddress.city}
          </Typography>
        </div>
        {couponAbovePayment && <div>{couponAccordion}</div>}
        {checkout?.total > 0 ? (
          <PaymentSection
            setStripe={setStripe}
            setStripeElements={setStripeElements}
            paymentMethodOrder={paymentMethodOrder}
          />
        ) : null}
        <div>
          <Typography variant="h6" gutterBottom>
            Bestellübersicht
          </Typography>
          <List disablePadding>
            {checkout?.lineItems?.map((lineItem) => {
              const couponAmount = Number(lineItem?.couponAmount || 0);
              const extendedPrice = Number(lineItem?.extendedPrice || 0);
              const originalLineTotal = Number(
                lineItem?.salePrice || lineItem?.price || (extendedPrice + couponAmount),
              );
              const showStrike = couponAmount > 0 && Number.isFinite(originalLineTotal)
                && Math.abs(originalLineTotal - extendedPrice) > 0.009;

              return (
                <ListItem sx={{ padding: 1, paddingLeft: 0 }} key={lineItem.id}>
                  <ListItemText
                    primary={`${lineItem.quantity} x ${lineItem.product.name}`}
                    secondary={(
                      <Stack spacing={0.5}>
                        <Typography variant="body2">
                          {`Teilnehmer: ${lineItem.attendees.map(({ forename, name }) => `${forename} ${name}`).join(', ')}`}
                        </Typography>
                        {couponAmount > 0 && (
                          <Typography variant="body2" sx={{ color: 'success.main' }}>
                            {`${t('coupon.applied')}: ${lineItem.coupons?.[0]?.name || ''} (-${formatNumber(couponAmount)})`}
                          </Typography>
                        )}
                      </Stack>
                    )}
                  />
                  <Stack alignItems="flex-end" spacing={0.25}>
                    {showStrike && (
                      <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                        {formatNumber(originalLineTotal)}
                      </Typography>
                    )}
                    <Typography variant="body2">{formatNumber(lineItem.extendedPrice)}</Typography>
                  </Stack>
                </ListItem>
              );
            })}
            {!couponAbovePayment && (
              <ListItem sx={{ padding: 1, paddingLeft: 0 }}>
                {couponAccordion}
              </ListItem>
            )}
            {isNetPriceModel ? (
              <>
                <ListItem sx={{ padding: 1, paddingLeft: 0 }}>
                  <ListItemText primary="Nettobetrag" />
                  <Typography variant="subtitle1">
                    {formatNumber(checkout?.subtotal)}
                  </Typography>
                </ListItem>
                <ListItem sx={{ padding: 1, paddingTop: 0, paddingLeft: 0 }}>
                  <ListItemText primary={taxLabel} />
                  <Typography variant="subtitle1">
                    {formatNumber(checkout?.taxAmount)}
                  </Typography>
                </ListItem>
                <ListItem sx={{ padding: 1, paddingLeft: 0, paddingBottom: 0 }}>
                  <ListItemText primary="Gesamtbetrag" primaryTypographyProps={{ sx: { fontWeight: 700 } }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {formatNumber(checkout?.total)}
                  </Typography>
                </ListItem>
              </>
            ) : (
              <>
                <ListItem sx={{ padding: 1, paddingLeft: 0, paddingBottom: 0 }}>
                  <ListItemText primary="Gesamtbetrag" primaryTypographyProps={{ sx: { fontWeight: 700 } }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {formatNumber(checkout?.total)}
                  </Typography>
                </ListItem>
                <ListItem sx={{ padding: 1, paddingTop: 0, paddingLeft: 0 }}>
                  <ListItemText primary={taxLabel} />
                  <Typography variant="subtitle1">
                    {formatNumber(checkout?.taxAmount)}
                  </Typography>
                </ListItem>
              </>
            )}
            <ListItem sx={{ padding: 1, paddingLeft: 0 }}>
              <InputField type="checkbox" name="accept" label={tosLabel} required />
            </ListItem>
          </List>
        </div>
      </Stack>
    </Box>
  );
}
